import React from 'react';
import { Box, Chip, Stack, Paper, Divider, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version
import { Typo } from 'components/primitives';
import { ResourceType } from 'interfaces/ResourceType';
import useStyles from './ProvidedResources.styles';
import { IRequestAmbulance } from 'interfaces/ResourceRequests';
import { ISelectProp } from 'interfaces/SelectProp';
import { AmbulanceOptions } from 'common/selectoptions';
import { IResponsesDB } from 'interfaces/response.interface';
import moment from 'moment/moment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import theme from '../../../theming/theme';

const selectLabel = (
  value: string | undefined,
  SelectOptions: ISelectProp[],
): string | undefined => {
  if (value) {
    const findValue = SelectOptions.find((m: ISelectProp) => m.value === value);
    return findValue?.label;
  } else {
    return undefined;
  }
};

const AmbulanceHeader: React.FC<{
  name: ResourceType;
  index: number;
  ambulance: IRequestAmbulance;
  responses: IResponsesDB[] | undefined;
}> = ({ index, ambulance, responses }) => {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const ttlRequested = ambulance.number || 0;
  const accepted =
    (ambulance && ambulance?.items && ambulance.items.filter((a) => a.accepted)) || [];

  const ttlAccepted = (accepted || []).reduce(
    (sum: number, item) => sum + (item && item.number ? item.number : 0),
    0,
  );

  return (
    <React.Fragment key={index}>
      <Box className={classes.contentStackView}>
        {/*  Headers  */}
        <Grid
          container
          columnSpacing={1}
          rowSpacing={1}
          sx={{ width: ttlAccepted ? 'auto' : '100%' }}>
          <Grid xs={12} sm={6} md={6} lg={6}>
            <Typo className={classes.resourceTitleText}>
              {selectLabel(ambulance.vehicleDescription, AmbulanceOptions)}
            </Typo>
          </Grid>
          <Grid
            xs={12}
            sm={6}
            md={6}
            lg={6}
            style={{ padding: '2px 4px 0px 4px', display: 'block' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box className={classes.requestAcceptBox} sx={{ marginRight: '6px' }}>
                <Typo className={classes.requestAcceptLabel}>Requested</Typo>
                <Typo className={classes.requestAcceptText}>{ttlRequested}</Typo>
              </Box>
              <Box className={classes.requestAcceptBox} sx={{ marginLeft: '6px' }}>
                <Typo className={classes.requestAcceptLabel}>Total accepted</Typo>
                <Typo className={classes.requestAcceptText}>{ttlAccepted}</Typo>
              </Box>
            </Box>
          </Grid>
          <Grid xs={12} sm={12} md={12} lg={12} style={{ marginTop: '12px' }}>
            {ambulance.items && ambulance.items.length ? (
              ambulance.items
                .filter((item) => item.accepted)
                .map((item, idx: number) => {
                  const response = responses?.find((r) => r.id === item.responsesId);
                  return response && item.number ? (
                    <Paper elevation={0} className={classes.detailItem} variant='outlined'>
                      <Grid container columnSpacing={1} rowSpacing={1}>
                        <Grid xs={9} sm={9} md={9} lg={9}>
                          <Typo className={classes.agencyNameText}>
                            {response.agency.agencyName}
                          </Typo>
                        </Grid>
                        <Grid xs={3} sm={3} md={3} lg={3} style={{ textAlign: 'right' }}>
                          <Chip
                            color='error'
                            size='small'
                            icon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
                            sx={{
                              backgroundColor: '#2961FD',
                              color: '#fefefe',
                              fontSize: '12px',
                              borderRadius: '4px',
                              height: '26px',
                            }}
                            label={item.number + (!isMobile ? ' Accepted' : '')}
                          />
                        </Grid>
                        <Grid xs={12} sm={6} md={4} lg={4} sx={{ display: 'flex' }}>
                          <div className={classes.detailsLabelText}>Offered: </div>
                          <div className={classes.detailsText}>
                            {moment(response?.createdAt).format('MM/DD/YYYY hh:mm A')}
                          </div>
                        </Grid>
                        <Grid xs={12} sm={6} md={4} lg={4} sx={{ display: 'flex' }}>
                          <div className={classes.detailsLabelText}>Accepted: </div>
                          <div className={classes.detailsText}>
                            {moment(response?.updatedAt).format('MM/DD/YYYY hh:mm A')}
                          </div>
                        </Grid>
                        <Grid xs={12} sm={12} md={4} lg={4} sx={{ display: 'flex' }}>
                          <Box className={classes.contactBox}>
                            <div className={classes.detailsLabelText}>Contact: </div>
                            <div className={classes.detailsText}>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <div style={{ whiteSpace: 'nowrap' }}>
                                  {response.primaryContactPerson}
                                </div>
                                <div style={{ color: '#2961FD', fontSize: '9px', margin: '2px' }}>
                                  {' '}
                                  {'\u25A0'}
                                </div>
                                <div style={{ whiteSpace: 'nowrap' }}>
                                  {response.primaryContactPhone}
                                </div>
                              </Box>
                            </div>
                          </Box>
                        </Grid>
                        <Grid xs={12} sm={12} md={12} lg={12}>
                          <Divider
                            sx={{ marginLeft: '-16px', marginRight: '-16px', paddingTop: '4px' }}
                          />
                          <div className={classes.detailsLabelText} style={{ marginTop: '8px' }}>
                            Comment{' '}
                          </div>
                        </Grid>
                        <Grid xs={12} sm={12} md={12} lg={12}>
                          <div className={classes.commentsText}>{response.primaryContactNotes}</div>
                        </Grid>
                      </Grid>
                    </Paper>
                  ) : (
                    <></>
                  );
                })
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
      </Box>
    </React.Fragment>
  );
};

export default AmbulanceHeader;
